import type { ComponentProps, ReactNode } from "react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { AnnotationCoords } from "@alaffia-technology-solutions/components";
import type {
  PDFAnnotationFormData,
  PDFAnnotationType,
  PDFAnnot,
  PDFPoint,
  PDFRatioCoords,
  PDFSidebarTab,
  PDFViewMode,
} from "@alaffia-technology-solutions/pdf-viewer";
import type { File } from "@alaffia-technology-solutions/schema";
import type { Awaitable } from "@alaffia-technology-solutions/types";

import { FileNotReadyTooltip } from "@alaffia-technology-solutions/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@alaffia-technology-solutions/ds";
import {
  mapFoxitRectToHighlightCoords,
  mapHighlightToRatioCoords,
  mapRatioToHighlightCoords,
  PDFAnnotation,
  PDFAnnotationForm,
  PDFAnnotations,
  PDFBookmarks,
  PDFSidebar,
  PDFTextActions,
  PDFTextSelectionTooltip,
  PDFThumbnails,
  PDFToolbar,
  PDFViewerCanvas,
  SearchResults,
  usePDFViewerEvent,
} from "@alaffia-technology-solutions/pdf-viewer";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import {
  IconBookmark,
  IconCarouselVertical,
  IconFileSearch,
  IconMessage,
} from "@tabler/icons-react";

import {
  useAnnotationLabels,
  useCreateFileUserAnnotation,
  useDeleteFileUserAnnotation,
  useFileUserAnnotations,
} from "../../files";
import { applyAnnotationHighlights } from "./applyAnnotationHighlights";
import { usePDFViewer } from "./usePDFViewer";

export const sidebarTabsKeys = [
  "annotations",
  "bookmarks",
  "search-results",
  "thumbnails",
] as const;

export type SidebarTabsKey = (typeof sidebarTabsKeys)[number];

export interface PDFTextSelectionCitation {
  fileId: File["id"];
  fileName?: string;
  pageIndex: number;
  sourceText: string;
  coordinates: PDFRatioCoords[];
}

export type PDFTextSelectionEvidenceIntent =
  | "current"
  | "disqualifying"
  | "save-for-later"
  | "supporting";

export interface PDFEvidenceHighlight {
  id: string;
  color: string;
  coordinates: PDFRatioCoords[];
  fileId?: File["id"];
  fileName?: string;
  linked?: boolean;
  pageIndex: number;
  sourceText?: string;
  evidenceIndex?: number;
  variant?: "supporting" | "disqualifying";
}

export interface PDFEvidenceHighlightLocateRequest {
  coordinates: PDFRatioCoords[];
  pageIndex: number;
  requestKey: string;
}

export interface PDFEvidenceLinkTarget {
  id: string;
  index: number;
  title: string;
  variant: "supporting" | "disqualifying";
}

const colors = {
  annotation: {
    default: "0xEFCCC2",
    active: "0xD77870",
  },
};

export interface PDFViewerProps {
  activeAnnotationId?: string;
  annotations?: AnnotationCoords[];
  className?: string;
  fileId: File["id"];
  fileName?: string;
  evidenceHighlights?: PDFEvidenceHighlight[];
  evidenceHighlightLocateRequest?: PDFEvidenceHighlightLocateRequest | null;
  evidenceLinkTargets?: PDFEvidenceLinkTarget[];
  currentEvidenceLinkTarget?: PDFEvidenceLinkTarget | null;
  highlights?: string[];
  hideAnnotationsSidebar?: boolean;
  hideUserAnnotations?: boolean;
  isSearchLoading?: boolean;
  onActiveAnnotationChange: (annotationId: string | null) => Awaitable<void>;
  onEvidenceHighlightAdd?: (
    highlight: PDFEvidenceHighlight,
    intent: Extract<
      PDFTextSelectionEvidenceIntent,
      "supporting" | "disqualifying"
    >,
  ) => void;
  onEvidenceHighlightClick?: (highlight: PDFEvidenceHighlight) => void;
  onEvidenceHighlightDelete?: (highlight: PDFEvidenceHighlight) => void;
  onEvidenceHighlightLink?: (
    highlight: PDFEvidenceHighlight,
    target: PDFEvidenceLinkTarget,
  ) => void;
  onEvidenceHighlightUnlink?: (highlight: PDFEvidenceHighlight) => void;
  onPageClick?: () => void;
  onResultPick?: (id?: string) => void;
  onSearch: (query: string) => void;
  onSidebarTabChange?: (tab: SidebarTabsKey | null) => Awaitable<void>;
  onPageChange?: (pageNumber: number) => void;
  onViewModeChange?: (viewMode: PDFViewMode) => void;
  onTextSelectionAdd?: (
    selection: PDFTextSelectionCitation,
    intent: PDFTextSelectionEvidenceIntent,
    target?: PDFEvidenceLinkTarget,
  ) => Awaitable<void>;
  page?: number;
  searchEnabled?: boolean;
  searchQuery: string;
  searchResults: ComponentProps<typeof SearchResults>["results"];
  sidebarTab?: SidebarTabsKey;
  textSelectionBanner?: ReactNode;
  textSelectionCanAdd?: boolean;
  textSelectionMode?: "annotation" | "evidence";
  url: string;
  viewMode?: PDFViewMode;
}

export const PDFViewer = ({
  activeAnnotationId,
  annotations,
  className,
  fileId,
  fileName,
  evidenceHighlights,
  evidenceHighlightLocateRequest,
  evidenceLinkTargets = [],
  currentEvidenceLinkTarget = null,
  highlights,
  hideAnnotationsSidebar = false,
  hideUserAnnotations = false,
  isSearchLoading,
  onActiveAnnotationChange,
  onEvidenceHighlightAdd,
  onEvidenceHighlightClick,
  onEvidenceHighlightDelete,
  onEvidenceHighlightLink,
  onEvidenceHighlightUnlink,
  onPageClick,
  onResultPick,
  onSearch: extOnSearch,
  onSidebarTabChange,
  onPageChange,
  onTextSelectionAdd,
  onViewModeChange,
  page,
  searchEnabled,
  searchQuery,
  searchResults,
  sidebarTab,
  textSelectionBanner,
  textSelectionCanAdd = true,
  textSelectionMode = "annotation",
  url,
  viewMode: controlledViewMode,
}: PDFViewerProps) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const lastPointerPositionRef = useRef({ left: 0, top: 0 });
  const [activeHighlightAction, setActiveHighlightAction] = useState<{
    highlight: PDFEvidenceHighlight;
    left: number;
    top: number;
  } | null>(null);
  const { data: fileAnnotations } = useFileUserAnnotations({ fileId });
  const activeAnnotation = useMemo(
    () => fileAnnotations?.find((a) => a.id === activeAnnotationId),
    [fileAnnotations, activeAnnotationId],
  );

  const pageIndex = page !== undefined ? page - 1 : undefined;

  const handleAnnotationLocate = async (
    annotation: PDFAnnotationType,
    offset?: PDFPoint,
  ) => {
    await onSidebarTabChange?.("annotations");
    await onActiveAnnotationChange(annotation.id);

    const offsetObject = await getAnnotationOffset(annotation);

    if (annotation?.pageIndex > 0) {
      await zoom("fitWidth");
      await jumpToPage(annotation.pageIndex - 1, offset ?? offsetObject);
    }
  };

  const handleTextHighlights = async () => {
    if (!highlights) {
      return;
    }

    await highlightTextOnPage(pageIndex ?? 0, highlights);
  };

  const handleOpenPDF = async () => {
    if (
      !hideUserAnnotations &&
      activeAnnotation &&
      (pageIndex === undefined || activeAnnotation.pageIndex - 1 === pageIndex)
    ) {
      const offset = await getAnnotationOffset(activeAnnotation);
      await jumpToPage(activeAnnotation.pageIndex - 1, offset);
    } else if (pageIndex !== undefined) {
      await jumpToPage(pageIndex);
    }
    await handleTextHighlights();
    if (controlledViewMode) {
      setViewMode(controlledViewMode);
    }
  };

  const handleViewModeChange = (viewMode: PDFViewMode) => {
    setViewMode(viewMode);
    onViewModeChange?.(viewMode);
  };

  const {
    addHighlight,
    currentPage,
    events,
    getPageThumbnail,
    jumpToPage,
    isDisabled,
    nextPage,
    pagesCount,
    prevPage,
    readOnlyScale,
    rotate,
    viewMode,
    setViewMode,
    state: pdfState,
    zoom,
    onSearch,
    bookmarks,
    textSelection,
    clearTextSelection,
    copyTextSelection,
    getPageDimensions,
    highlightTextOnPage,
    removeHighlights,
  } = usePDFViewer({
    onSearch: extOnSearch,
    searchQuery,
  });

  const annotationHighlightsRef = useRef<PDFAnnot[]>([]);
  const annotationHighlightsRevisionRef = useRef(0);

  const handleAnnotationsHighlight = useCallback(
    async (revision: number) => {
      await applyAnnotationHighlights({
        previous: annotationHighlightsRef.current,
        annotations,
        pageIndex,
        addHighlight,
        getPageDimensions,
        removeHighlights,
        isCurrent: () => revision === annotationHighlightsRevisionRef.current,
        commit: (highlights) => {
          annotationHighlightsRef.current = highlights;
        },
      });
    },
    [annotations, pageIndex, addHighlight, getPageDimensions, removeHighlights],
  );

  useEffect(() => {
    // Bump the revision so any in-flight invocation becomes stale and can no
    // longer commit (or leave stale highlights behind).
    const revision = ++annotationHighlightsRevisionRef.current;

    if (pdfState !== "opened") {
      annotationHighlightsRef.current = [];
      return;
    }

    void handleAnnotationsHighlight(revision).catch(console.error);
  }, [pdfState, annotations, pageIndex, handleAnnotationsHighlight]);

  const [annotationFormOpen, setAnnotationFormOpen] = useState(false);
  const { data: annotationLabels } = useAnnotationLabels();
  const evidenceHighlightsForFile = useMemo(
    () =>
      evidenceHighlights?.filter(
        (highlight) =>
          highlight.fileId === undefined ||
          String(highlight.fileId) === String(fileId),
      ),
    [evidenceHighlights, fileId],
  );
  const handleEvidenceHighlightActionClick = useCallback(
    (highlight: PDFEvidenceHighlight) => {
      onEvidenceHighlightClick?.(highlight);
      setActiveHighlightAction({
        highlight,
        ...lastPointerPositionRef.current,
      });
    },
    [onEvidenceHighlightClick],
  );

  useEffect(() => {
    if (!evidenceHighlightLocateRequest || pdfState !== "opened") return;

    const locateHighlight = async () => {
      const targetPageIndex = evidenceHighlightLocateRequest.pageIndex - 1;
      const firstCoord = evidenceHighlightLocateRequest.coordinates[0];

      // Page-only annotations (e.g. AI-generated citations that carry a page
      // number but no rectangle) still need to scroll the viewer to the page.
      if (!firstCoord) {
        await zoom("fitWidth");
        await jumpToPage(targetPageIndex);
        return;
      }

      const pageDimensions = await getPageDimensions(targetPageIndex);
      if (!pageDimensions) {
        await zoom("fitWidth");
        await jumpToPage(targetPageIndex);
        return;
      }

      const offset = mapRatioToHighlightCoords(firstCoord, pageDimensions);
      await zoom("fitWidth");
      await jumpToPage(targetPageIndex, {
        x: offset.lowerLeft.x,
        y: offset.upperRight.y,
      });
    };

    void locateHighlight();
  }, [
    evidenceHighlightLocateRequest,
    getPageDimensions,
    jumpToPage,
    pdfState,
    zoom,
  ]);

  useEvidenceHighlights({
    addHighlight,
    evidenceHighlights: evidenceHighlightsForFile,
    events,
    getPageDimensions,
    onEvidenceHighlightClick: handleEvidenceHighlightActionClick,
    pdfState,
    removeHighlights,
  });

  const handleCloseAnnotation = useCallback(() => {
    clearTextSelection();
    setAnnotationFormOpen(false);
  }, [clearTextSelection]);

  const { mutate: createFileAnnotation } = useCreateFileUserAnnotation({
    fileId,
    onSuccess: handleCloseAnnotation,
  });

  const { mutate: deleteFileAnnotation } = useDeleteFileUserAnnotation({
    fileId,
  });

  const getAnnotationOffset = useCallback(
    async (annotation: PDFAnnotationType | undefined) => {
      if (!annotation) return;

      const firstCoord = annotation.coordinates?.[0];
      if (!firstCoord) return;

      const pageDimensions = await getPageDimensions?.(
        (annotation.pageIndex ?? 1) - 1,
      );

      if (!pageDimensions) return;

      const offset = mapRatioToHighlightCoords(firstCoord, pageDimensions);

      return {
        x: offset.lowerLeft.x,
        y: offset.upperRight.y,
      };
    },
    [getPageDimensions],
  );

  const sidebarTabs = [
    {
      key: "thumbnails",
      icon: <IconCarouselVertical className="af-size-4" />,
      title: "Thumbnails",
      content: (
        <PDFThumbnails
          jumpToPage={jumpToPage}
          fileId={fileId}
          pagesCount={pagesCount}
          currentPage={currentPage}
          getPageThumbnail={getPageThumbnail}
        />
      ),
      triggerProps: {
        disabled: isDisabled,
      },
    },
    {
      key: "search-results",
      icon: <IconFileSearch className="af-size-4" />,
      title: "Search Results",
      content: (
        <SearchResults
          onResultPick={onResultPick}
          query={searchQuery}
          results={searchResults}
          isLoading={isSearchLoading}
        />
      ),
      triggerProps: {
        disabled: isDisabled || !searchEnabled,
      },
    },
    {
      key: "bookmarks",
      icon: <IconBookmark className="af-size-4" />,
      title: "Bookmarks",
      content: (
        <PDFBookmarks
          bookmarks={bookmarks ?? []}
          onBookmarkClick={(bookmark) =>
            bookmark.page !== undefined && jumpToPage(bookmark.page)
          }
        />
      ),
      triggerProps: {
        disabled: isDisabled || !bookmarks || bookmarks.length === 0,
      },
    },
    {
      key: "annotations",
      icon: <IconMessage className="af-size-4" />,
      title: "Annotations",
      content: (
        <PDFAnnotations
          annotations={fileAnnotations ?? []}
          activeAnnotationId={activeAnnotationId}
          getPageDimensions={getPageDimensions}
          onAnnotationClick={async (annotation, offset) =>
            handleAnnotationLocate(annotation, offset)
          }
          onDeleteClick={(annotation) =>
            deleteFileAnnotation({ id: annotation.id })
          }
        />
      ),
      triggerProps: {
        disabled: isDisabled,
      },
    },
  ].filter(
    (tab) => !(hideAnnotationsSidebar && tab.key === "annotations"),
  ) as PDFSidebarTab<SidebarTabsKey>[];

  const handleTabChange = async (tab: SidebarTabsKey | null) => {
    await onSidebarTabChange?.(tab);
    return zoom("fitWidth");
  };

  const handleSearch = async (query: string) => {
    await handleTabChange("search-results");
    return onSearch(query);
  };

  const handleAnnotationFormSubmit = async (data: PDFAnnotationFormData) => {
    if (!textSelection) {
      throw new Error(
        "Expected textSelection to exist when submitting annotation form",
      );
    }

    const pageDimensions = await getPageDimensions(textSelection.pageIndex);

    if (!pageDimensions) {
      throw new Error("Failed to get pageDimensions");
    }

    createFileAnnotation({
      fileId,
      input: {
        comment: data.comment,
        pageIndex: textSelection.pageIndex + 1,
        labels: data.labels,
        sourceText: textSelection.text,
        coordinates: textSelection.rects.map((r) => {
          const foxitRect = mapFoxitRectToHighlightCoords(r);
          const ratioCoords = mapHighlightToRatioCoords(
            foxitRect,
            pageDimensions,
          );
          return ratioCoords;
        }),
      },
    });
  };

  const handleAddTextSelection = async (
    intent: PDFTextSelectionEvidenceIntent = "current",
    target?: PDFEvidenceLinkTarget,
  ) => {
    if (!textSelection) return;

    const pageDimensions = await getPageDimensions(textSelection.pageIndex);

    if (!pageDimensions) {
      throw new Error("Failed to get pageDimensions");
    }

    await onTextSelectionAdd?.(
      {
        fileId,
        fileName,
        pageIndex: textSelection.pageIndex + 1,
        sourceText: textSelection.text,
        coordinates: textSelection.rects.map((rect) =>
          mapHighlightToRatioCoords(
            mapFoxitRectToHighlightCoords(rect),
            pageDimensions,
          ),
        ),
      },
      intent,
      target,
    );
    clearTextSelection();
  };

  return (
    <div
      ref={viewerRef}
      className={cn(
        "af-pdf-viewer af-relative af-flex af-h-full af-w-full af-flex-col af-overflow-hidden af-bg-background",
        className,
      )}
      onPointerDownCapture={(event) => {
        const rect = viewerRef.current?.getBoundingClientRect();
        if (!rect) return;

        lastPointerPositionRef.current = {
          left: event.clientX - rect.left,
          top: event.clientY - rect.top,
        };
      }}
    >
      <div className="af-relative af-flex af-h-full af-flex-col af-overflow-hidden af-border-y af-border-border af-bg-background">
        <PDFToolbar
          currentPage={currentPage}
          defaultSearchValue={searchQuery}
          disabled={isDisabled}
          onJumpToPage={jumpToPage}
          onNextPage={nextPage}
          onPrevPage={prevPage}
          onRotate={rotate}
          onSearch={handleSearch}
          onViewModeChange={handleViewModeChange}
          onZoom={zoom}
          pagesCount={pagesCount}
          searchEnabled={searchEnabled}
          searchTooltip={<FileNotReadyTooltip />}
          url={url}
          viewMode={viewMode}
          zoomScale={readOnlyScale}
        />
        {textSelectionBanner}
        <div className="af-m-1 af-flex af-h-full af-overflow-hidden af-bg-background">
          <PDFSidebar
            tabs={sidebarTabs}
            selectedTab={sidebarTab ?? null}
            onTabChange={handleTabChange}
          />
          <PDFViewerCanvas
            url={url}
            fileId={fileId}
            pageIndex={pageIndex}
            onOpenFile={handleOpenPDF}
            onPageClick={() => {
              setActiveHighlightAction(null);
              onPageClick?.();
              if (!hideUserAnnotations) void onActiveAnnotationChange(null);
            }}
            onPageChange={onPageChange}
          >
            {!hideUserAnnotations &&
              fileAnnotations?.map(
                (annotation) =>
                  annotation.coordinates && (
                    <PDFAnnotation
                      key={annotation.id}
                      color={
                        annotation.id === activeAnnotationId
                          ? colors.annotation.active
                          : colors.annotation.default
                      }
                      pageIndex={annotation.pageIndex - 1}
                      coordinates={annotation.coordinates}
                      onClick={() => handleAnnotationLocate(annotation)}
                    />
                  ),
              )}
            <PDFTextSelectionTooltip
              open={Boolean(textSelection)}
              onClose={handleCloseAnnotation}
            >
              {textSelectionMode === "evidence" ? (
                <PDFTextActions
                  evidenceCardOpen={textSelectionCanAdd}
                  linkTargets={evidenceLinkTargets}
                  linked={null}
                  selection="new"
                  onAddAsDisqualifying={() =>
                    void handleAddTextSelection("disqualifying")
                  }
                  onAddAsSupporting={() =>
                    void handleAddTextSelection("supporting")
                  }
                  onAddToEvidence={() => void handleAddTextSelection("current")}
                  onLink={(target) =>
                    void handleAddTextSelection("current", target)
                  }
                  onMore={() => undefined}
                  onCopy={async () => {
                    await copyTextSelection()?.then(() => {
                      toast.info("Text copied to clipboard");
                      clearTextSelection();
                    });
                  }}
                  onSaveForLater={() =>
                    void handleAddTextSelection("save-for-later")
                  }
                />
              ) : annotationFormOpen ? (
                <PDFAnnotationForm
                  annotationLabels={annotationLabels}
                  onSubmit={handleAnnotationFormSubmit}
                />
              ) : (
                <PDFTextActions
                  onAddAnnotation={() => setAnnotationFormOpen(true)}
                  onCopy={async () => {
                    await copyTextSelection()?.then(() => {
                      toast.info("Text copied to clipboard");
                      clearTextSelection();
                    });
                  }}
                />
              )}
            </PDFTextSelectionTooltip>
            <Popover
              open={activeHighlightAction !== null}
              onOpenChange={(open) => {
                if (!open) setActiveHighlightAction(null);
              }}
            >
              <PopoverTrigger asChild>
                <div
                  className="af-pointer-events-none af-absolute af-h-px af-w-px"
                  style={{
                    left: activeHighlightAction?.left ?? 0,
                    top: activeHighlightAction?.top ?? 0,
                  }}
                />
              </PopoverTrigger>
              <PopoverContent
                className="af-w-fit af-rounded-lg af-border af-border-interface-border af-bg-card af-p-1.5 af-shadow-[0px_25px_25px_rgba(42,100,246,0.1)]"
                side="top"
                hideWhenDetached
              >
                {activeHighlightAction && (
                  <PDFTextActions
                    evidenceCardOpen={textSelectionCanAdd}
                    linkTargets={evidenceLinkTargets}
                    linked={activeHighlightAction.highlight.linked ?? false}
                    selection="old"
                    onAddAsDisqualifying={() => {
                      onEvidenceHighlightAdd?.(
                        activeHighlightAction.highlight,
                        "disqualifying",
                      );
                      setActiveHighlightAction(null);
                    }}
                    onAddAsSupporting={() => {
                      onEvidenceHighlightAdd?.(
                        activeHighlightAction.highlight,
                        "supporting",
                      );
                      setActiveHighlightAction(null);
                    }}
                    onAddToEvidence={() => {
                      if (currentEvidenceLinkTarget) {
                        onEvidenceHighlightLink?.(
                          activeHighlightAction.highlight,
                          currentEvidenceLinkTarget,
                        );
                      }
                      setActiveHighlightAction(null);
                    }}
                    onDelete={() => {
                      onEvidenceHighlightDelete?.(
                        activeHighlightAction.highlight,
                      );
                      setActiveHighlightAction(null);
                    }}
                    onCopy={() => {
                      void navigator.clipboard
                        .writeText(
                          activeHighlightAction.highlight.sourceText ?? "",
                        )
                        .then(() => {
                          toast.info("Text copied to clipboard");
                          setActiveHighlightAction(null);
                        });
                    }}
                    onLink={(target) => {
                      onEvidenceHighlightLink?.(
                        activeHighlightAction.highlight,
                        target,
                      );
                      setActiveHighlightAction(null);
                    }}
                    onMore={() => undefined}
                    onUnlink={() => {
                      onEvidenceHighlightUnlink?.(
                        activeHighlightAction.highlight,
                      );
                      setActiveHighlightAction(null);
                    }}
                  />
                )}
              </PopoverContent>
            </Popover>
          </PDFViewerCanvas>
        </div>
      </div>
    </div>
  );
};

interface UseEvidenceHighlightsOptions {
  addHighlight: ReturnType<typeof usePDFViewer>["addHighlight"];
  evidenceHighlights?: PDFEvidenceHighlight[];
  events: ReturnType<typeof usePDFViewer>["events"];
  getPageDimensions: ReturnType<typeof usePDFViewer>["getPageDimensions"];
  onEvidenceHighlightClick?: (highlight: PDFEvidenceHighlight) => void;
  pdfState: ReturnType<typeof usePDFViewer>["state"];
  removeHighlights: ReturnType<typeof usePDFViewer>["removeHighlights"];
}

function useEvidenceHighlights({
  addHighlight,
  evidenceHighlights,
  events,
  getPageDimensions,
  onEvidenceHighlightClick,
  pdfState,
  removeHighlights,
}: UseEvidenceHighlightsOptions) {
  const highlightAnnotsRef = useRef(new Map<string, PDFAnnot>());
  const highlightColorsRef = useRef(new Map<string, string>());
  const highlightIdByAnnotIdRef = useRef(new Map<string, string>());
  const desiredHighlightsRef = useRef(new Map<string, PDFEvidenceHighlight>());

  usePDFViewerEvent(
    events?.tapAnnotation,
    (annotRender: { getAnnot: () => PDFAnnot }) => {
      const annot = annotRender.getAnnot();
      const highlightId = highlightIdByAnnotIdRef.current.get(
        annot.getUniqueID(),
      );
      const highlight = highlightId
        ? desiredHighlightsRef.current.get(highlightId)
        : undefined;

      if (highlight) {
        onEvidenceHighlightClick?.(highlight);
      }
    },
  );

  useEffect(() => {
    const nextHighlights = new Map(
      (evidenceHighlights ?? []).map((highlight) => [highlight.id, highlight]),
    );
    desiredHighlightsRef.current = nextHighlights;

    if (pdfState !== "opened") {
      highlightAnnotsRef.current.clear();
      highlightColorsRef.current.clear();
      highlightIdByAnnotIdRef.current.clear();
      return;
    }

    const syncHighlights = async () => {
      const staleAnnots = Array.from(highlightAnnotsRef.current.entries())
        .filter(([id]) => !desiredHighlightsRef.current.has(id))
        .map(([, annot]) => annot);

      if (staleAnnots.length > 0) {
        await removeHighlights(staleAnnots);
        for (const [id] of Array.from(highlightAnnotsRef.current.entries())) {
          if (!desiredHighlightsRef.current.has(id)) {
            highlightAnnotsRef.current.delete(id);
            highlightColorsRef.current.delete(id);
            for (const [annotId, highlightId] of Array.from(
              highlightIdByAnnotIdRef.current.entries(),
            )) {
              if (highlightId === id) {
                highlightIdByAnnotIdRef.current.delete(annotId);
              }
            }
          }
        }
      }

      for (const highlight of nextHighlights.values()) {
        const existingAnnot = highlightAnnotsRef.current.get(highlight.id);
        const existingColor = highlightColorsRef.current.get(highlight.id);

        if (existingAnnot) {
          if (existingColor !== highlight.color) {
            await existingAnnot.setBorderColor(highlight.color);
            highlightColorsRef.current.set(highlight.id, highlight.color);
          }
          continue;
        }

        const pageDimensions = await getPageDimensions(highlight.pageIndex - 1);
        if (!pageDimensions) continue;

        // Page-only annotations (e.g. AI-generated citations) carry no
        // rectangle and cannot be rendered as a highlight. Skip them so a
        // single coordinate-less highlight doesn't abort the rest of the sync.
        if (highlight.coordinates.length === 0) continue;

        const addedHighlight = await addHighlight(
          highlight.pageIndex - 1,
          highlight.coordinates.map((coords) =>
            mapRatioToHighlightCoords(coords, pageDimensions),
          ),
          { color: highlight.color },
        ).catch((error: unknown) => {
          console.error(
            `[PDFViewer] Failed to render highlight ${highlight.id}`,
            error,
          );
          return undefined;
        });

        if (!addedHighlight) continue;

        const latestHighlight = desiredHighlightsRef.current.get(highlight.id);
        if (!latestHighlight || highlightAnnotsRef.current.has(highlight.id)) {
          await removeHighlights([addedHighlight]);
          continue;
        }

        highlightAnnotsRef.current.set(highlight.id, addedHighlight);
        highlightColorsRef.current.set(highlight.id, latestHighlight.color);
        highlightIdByAnnotIdRef.current.set(
          addedHighlight.getUniqueID(),
          highlight.id,
        );

        if (latestHighlight.color !== highlight.color) {
          await addedHighlight.setBorderColor(latestHighlight.color);
        }
      }
    };

    void syncHighlights().catch(console.error);
  }, [
    addHighlight,
    evidenceHighlights,
    getPageDimensions,
    pdfState,
    removeHighlights,
  ]);

  useEffect(() => {
    return () => {
      const annots = Array.from(highlightAnnotsRef.current.values());
      highlightAnnotsRef.current.clear();
      highlightColorsRef.current.clear();
      highlightIdByAnnotIdRef.current.clear();
      void removeHighlights(annots);
    };
  }, [removeHighlights]);
}
